import {inject, Injectable} from '@angular/core';
import {combineLatest, forkJoin, Observable, of} from 'rxjs';
import {catchError, map, switchMap} from 'rxjs/operators';

import {SharedMyCheck, SharedProject, WorkflowExecutionEvent, WorkflowService} from '../../services/workflow.service';
import {MyCheckTemplateQuotaError, RulesetDuplicationResult, RulesetService,} from '../../services/ruleset.service';
import {BicToastService} from '@buildinginformationcloud/bic-ui-components';
import {WorkflowDefinition} from '../../api/model/workflowDefinition';
import {WorkflowInstanceDto} from '../../api/model/workflowInstanceDto';
import {Ruleset, RulesetType} from '../ruleManagement/models/rule-management.models';
import {WorkflowDefinitionWithCatalog} from '../../services/catalog.service';
import {
  buildManifestFromState,
  createDefaultBuilderState,
  parseManifestToState,
} from './my-checks-builder/my-checks-builder.model';
import {remapRulesetIds, RulesetIdMapping,} from './shared/duplicate-check-prompt/check-duplication.model';
import {interactiveBuilderStep} from './my-checks-builder/builder-schedule-eligibility';

export interface CheckDefinition {
  id: string;
  name: string;
  slug?: string;
  description: string;
  version: string;
  status: 'DRAFT' | 'ACTIVE' | 'INACTIVE' | 'ARCHIVED';
  visibility: 'PUBLIC' | 'PRIVATE';
  readOnly: boolean;
  checkType: RulesetType;
  /** Herkunft des Checks: vorgefertigt (BIC) oder benutzerdefiniert (myCheck). */
  origin: 'BIC' | 'CUSTOM';
  /** Anzahl der im Check enthaltenen Prüfregeln (best-effort aus dem Manifest). */
  ruleCount: number;
  /**
   * Der Eintrag ist eine Freigabe eines anderen Mandanten: nur duplizierbar, nicht öffenbar und
   * nicht ausführbar.
   *
   * <p>Hintergrund: eine Freigabe ist keine WorkflowDefinition, sondern ein eingefrorener Snapshot.
   * Es gibt dazu nichts zu öffnen und nichts auszuführen – erst das Duplikat ist ein echter Check.
   */
  shared?: boolean;
  /** Nur bei {@link shared}: ID der Freigabe (NICHT der Definition) – Ziel des Duplizierens. */
  shareId?: string;
  /** Eigentümerprojekt; bei zentral bereitgestellten Checks `undefined`. */
  projectId?: number;
  /**
   * Nur bei einem zwischen Projekten geteilten myCheck (Live-Verlinkung, nicht {@link shared}):
   * alle Projekte, in denen er sichtbar ist, Eigentümer zuerst.
   */
  sharedProjects?: SharedProject[];
  /** Für die Sortierung "Zuletzt geändert" – best-effort, bei Freigaben nur createdAt (Teilzeitpunkt). */
  createdAt?: string | Date;
  updatedAt?: string | Date;
  /** Nur bei {@link shared}: Katalog-Eintrag und -Version der Freigabe, falls über den Katalog installiert. */
  catalogKey?: string;
  catalogVersion?: number;
  /** Duplikat einer Katalog-Kopie: Katalog-Eintrag und -Version, aus der dupliziert wurde. */
  sourceCatalogKey?: string;
  sourceCatalogVersion?: number;
  /**
   * Builder-Schritt, in dem die beim Start abgefragte Auswahl festgelegt wird (siehe
   * `interactiveBuilderStep`): 1 = Regeln, 2 = Dateien, 0 = anderer Schritt. `null` = beim Start wird
   * nichts abgefragt, der Prüflauf ist also planbar. `undefined` = unbekannt (Freigaben ohne Manifest).
   */
  startSelectionStep?: number | null;
}

/** Ein dem Prüflauf zugeordnetes myCheck – Grundlage der Einzelauswahl im Duplizieren-Dialog. */
export interface AssignedRuleset {
  ruleset: Ruleset;
  /** Nur einzelne Prüfregeln des Sets sind zugeordnet. Kopiert wird trotzdem das ganze Set. */
  partial: boolean;
}

export interface DuplicatedCheck {
  definition: WorkflowDefinition;
  /** `false`: das Duplikat existiert, zeigt aber weiter auf die bereitgestellten myChecks. */
  rulesRelinked: boolean;
}

/**
 * Dedizierter Service für MyChecks.
 * Kapselt die Workflow-Details und bietet eine einfache API für die MyChecks-Komponenten.
 */
@Injectable({providedIn: 'root'})
export class MyChecksService {
  private flowService = inject(WorkflowService);
  private rulesetService = inject(RulesetService);
  private toastService = inject(BicToastService);

  /**
   * Alle verfügbaren Checks für ein Projekt laden. Blendet Archivierte aus.
   *
   * <p>Drei Töpfe: `prebuilt` (zentral bereitgestellt, PUBLIC), `custom` (projekt-eigen) und
   * `shared` (Freigaben anderer Mandanten). `shared` kommt aus einem <b>eigenen</b> Endpunkt und
   * nicht aus dem visibility-Split: Freigaben sind keine WorkflowDefinitions, und sie dürfen
   * bewusst nicht in `available` auftauchen – dort wären sie öffenbar und ausführbar.
   */
  getAvailableChecks(projectId: number): Observable<{
    prebuilt: CheckDefinition[];
    custom: CheckDefinition[];
    shared: CheckDefinition[];
  }> {
    return combineLatest([
      this.flowService.listAvailableDefinitions(projectId),
      this.flowService.listSharedForMe(projectId),
    ]).pipe(
      map(([defs, shares]) => ({
        prebuilt: defs.filter(d => d.visibility === 'PUBLIC').map(d => this.toCheckDefinition(d)),
        custom: defs.filter(d => d.visibility !== 'PUBLIC').map(d => this.toCheckDefinition(d)),
        shared: shares.map(share => this.toSharedCheckDefinition(share)),
      })),
    );
  }

  /** Eigenen myCheck duplizieren (serverseitig – ersetzt das frühere clientseitige Klonen). */
  duplicateCheck(id: string, projectId: number, copyRulesetIds: string[] = []): Observable<WorkflowDefinition> {
    return this.flowService.duplicateDefinition(id, projectId, copyRulesetIds);
  }

  /**
   * Die dem Prüflauf zugeordneten myChecks. Funktioniert auch für eine Freigabe: die referenzierten
   * myChecks werden beim Bereitstellen zentral sichtbar und liegen als `prebuilt`-Set im Projekt.
   *
   * <p>`null` = nicht lesbar; der Dialog fällt dann auf "nur den Prüflauf" zurück.
   */
  getAssignedRulesets(definitionId: string, projectId: number): Observable<AssignedRuleset[] | null> {
    return combineLatest([
      this.flowService.getDefinition(definitionId, projectId).pipe(
        map(def => this.readRuleSelection(def)),
        // Kein Toast: `null` zeigt der Dialog selbst als Ladefehler an (loadFailed).
        catchError(err => {
          console.error('[MyChecksService] Prüflauf für die myCheck-Auswahl nicht lesbar', err);
          return of(null);
        }),
      ),
      this.rulesetService.listRulesets(projectId).pipe(
        // Ohne die Liste fielen alle zugeordneten myChecks als „nicht sichtbar" heraus, und der
        // Dialog böte stumm nur den Prüflauf an – deshalb sichtbar melden.
        catchError(err => {
          console.error('[MyChecksService] myChecks des Projekts nicht lesbar', err);
          this.toastService.error('Die myChecks des Projekts konnten nicht geladen werden.');
          return of({prebuilt: [] as Ruleset[], custom: [] as Ruleset[]});
        }),
      ),
    ]).pipe(
      map(([selection, rulesets]) => {
        if (!selection) return null;
        const known = new Map([...rulesets.prebuilt, ...rulesets.custom].map(r => [r.id, r]));
        return selection.setIds
          .map(id => {
            // Unbekannte ID = ein Set, das dieses Projekt nicht sehen darf – nicht kopierbar.
            const ruleset = known.get(id);
            return ruleset ? {ruleset, partial: selection.partialSetIds.has(id)} : null;
          })
          .filter((entry): entry is AssignedRuleset => entry !== null);
      }),
    );
  }

  /**
   * Übernimmt eine Freigabe als eigenen Prüflauf und kopiert optional die gewählten myChecks mit.
   *
   * <p>Scheitert das Umbiegen der Regelauswahl, bleibt ein lauffähiges Duplikat mit den
   * ursprünglichen, schreibgeschützten Bezügen zurück – siehe {@link DuplicatedCheck.rulesRelinked}.
   */
  duplicateFromShareWithRules(
    shareId: string,
    projectId: number,
    rulesets: Ruleset[],
  ): Observable<DuplicatedCheck> {
    return this.assertTemplateQuota(projectId, rulesets.length).pipe(
      switchMap(() => this.flowService.duplicateFromShare(shareId, projectId)),
      switchMap(created => {
        if (rulesets.length === 0 || !created?.id) {
          return of({definition: created, rulesRelinked: true});
        }
        return forkJoin(rulesets.map(ruleset => this.rulesetService
          .duplicateRulesetWithMapping(ruleset, projectId, `${ruleset.name} (Kopie)`)
          .pipe(catchError(() => of(null))),
        )).pipe(
          switchMap(results => this.relinkRuleSelection(created, projectId, results)),
        );
      }),
    );
  }

  /**
   * Prüft vor der ersten Kopie, ob das Vorlagenkontingent für ALLE zu kopierenden myChecks reicht.
   *
   * <p>Die Übernahme legt die Regelsets nacheinander an. Ohne diese Vorabprüfung liefe sie in einen
   * 402 mitten in der Schleife und hinterließe eine halbe Übernahme: Prüflauf und ein Teil der
   * Kopien angelegt, der Rest nicht – nachträglich nicht mehr sauber aufzuräumen.
   *
   * <p>Ist der Stand nicht lesbar, wird nicht blockiert: das Backend lehnt notfalls selbst ab. Der
   * Nutzer erfährt es aber, weil die Vorabprüfung dann nicht greift und die Übernahme mitten in
   * der Schleife abbrechen kann.
   */
  private assertTemplateQuota(projectId: number, needed: number): Observable<void> {
    if (needed === 0) {
      return of(undefined);
    }
    return this.rulesetService.getQuota(projectId).pipe(
      catchError(err => {
        console.error('[MyChecksService] Vorlagenkontingent nicht lesbar', err);
        this.toastService.warning('Das Vorlagenkontingent konnte nicht geprüft werden. Reicht es nicht'
          + ' für alle myChecks, werden nur einige davon kopiert.');
        return of(null);
      }),
      map(quota => {
        if (quota?.remaining != null && quota.remaining < needed) {
          throw new MyCheckTemplateQuotaError(quota, needed);
        }
        return undefined;
      }),
    );
  }

  private relinkRuleSelection(
    created: WorkflowDefinition,
    projectId: number,
    results: (RulesetDuplicationResult | null)[],
  ): Observable<DuplicatedCheck> {
    const mapping = results.reduce<RulesetIdMapping>((acc, result) => {
      if (!result?.newId) return acc;
      acc.sets[result.sourceId] = result.newId;
      Object.assign(acc.rules, result.ruleIdMap);
      return acc;
    }, {sets: {}, rules: {}});

    if (Object.keys(mapping.sets).length === 0) {
      return of({definition: created, rulesRelinked: false});
    }
    // Ein myCheck, dessen Kopie scheiterte, bleibt auf das Original verlinkt – dann ist die
    // Zuordnung nur teilweise umgebogen und muss wie ein Fehlschlag gemeldet werden.
    const allCopied = results.every(result => !!result?.newId);

    // Über den Builder-Zustand statt per JSON-Chirurgie am Manifest: dort stecken die Regelset-IDs
    // an mehreren Stellen, buildManifestFromState schreibt sie alle konsistent neu.
    const state = {
      ...createDefaultBuilderState(),
      ...parseManifestToState(this.flowService.parseManifest(created)),
    };
    const manifest = buildManifestFromState(remapRulesetIds(state, mapping));

    return this.flowService.updateDefinition(created.id!, manifest, projectId).pipe(
      map(updated => ({definition: updated ?? created, rulesRelinked: allCopied})),
      catchError(() => of({definition: created, rulesRelinked: false})),
    );
  }

  private readRuleSelection(def: WorkflowDefinition): { setIds: string[]; partialSetIds: Set<string> } {
    const state = parseManifestToState(this.flowService.parseManifest(def));
    const partialSetIds = new Set(state.partiallySelectedRuleSetIds ?? []);
    // Teilweise ausgewählte Sets stehen nicht in selectedRuleSetIds, gehören aber dazu.
    const setIds = [...new Set([...(state.selectedRuleSetIds ?? []), ...partialSetIds])];
    return {setIds, partialSetIds};
  }

  /** Archivierte, benutzerdefinierte Checks eines Projekts (für die Archiv-Filteransicht). */
  getArchivedChecks(projectId: number): Observable<CheckDefinition[]> {
    return this.flowService.listDefinitions(projectId, true).pipe(
      map(({custom}) => custom
        .map(d => this.toCheckDefinition(d))
        .filter(d => d.status === 'ARCHIVED')),
    );
  }

  /** Archiviert einen myCheck (löscht nicht hart, siehe WorkflowService#archiveDefinition). */
  archiveCheck(id: string, projectId: number): Observable<{ [key: string]: string }> {
    return this.flowService.archiveDefinition(id, projectId);
  }

  /** Stellt einen archivierten myCheck wieder her (Status → INACTIVE). */
  restoreCheck(id: string, projectId: number): Observable<WorkflowDefinition> {
    return this.flowService.restoreDefinition(id, projectId);
  }

  /** Check per Slug starten (für BIC-Checks). */
  executeCheckBySlug(slug: string, projectId: number): Observable<WorkflowInstanceDto> {
    return this.flowService.getDefinitionBySlug(slug).pipe(
      switchMap(def => this.flowService.triggerWorkflow(def.id!, {}, projectId)),
    );
  }

  /**
   * Check per Definition-ID starten (für Custom-Checks).
   *
   * @param reportName Optionaler Berichtsname für diesen Lauf (siehe
   *   {@link WorkflowService#triggerWorkflow}).
   */
  executeCheckById(definitionId: string, projectId: number, triggerData?: Record<string, unknown>,
                   reportName?: string): Observable<WorkflowInstanceDto> {
    return this.flowService.triggerWorkflow(definitionId, triggerData ?? {}, projectId, reportName);
  }

  /** SSE-Events einer laufenden Instanz abonnieren. */
  subscribeToEvents(instanceId: string): Observable<WorkflowExecutionEvent> {
    return this.flowService.subscribeToExecutionEvents(instanceId);
  }

  /** Workflow mit User-Input fortsetzen (nach Regel-/Dateiauswahl). */
  resumeWithInput(instanceId: string, userInput: any): Observable<any> {
    return this.flowService.resumeWorkflow(instanceId, userInput);
  }

  /** Instanz-Status abfragen (Fallback wenn SSE abbricht). */
  getInstance(instanceId: string): Observable<WorkflowInstanceDto> {
    return this.flowService.getInstance(instanceId);
  }

  /** Laufenden Check abbrechen. */
  cancelCheck(instanceId: string): Observable<any> {
    return this.flowService.cancelInstance(instanceId);
  }

  /**
   * Credits beim Start des Prüflaufs abbuchen (Reservierung).
   * Der Aufrufer muss den 402-Fall (kein Kontingent) behandeln.
   */
  reserveCredits(instanceId: string, projectId: number): Observable<void> {
    return this.flowService.reserveMyCheckCredits(instanceId, projectId);
  }

  /** Abgebuchte Credits bei technischem Fehlschlag zurückerstatten (idempotent, backend-seitig FAILED-geprüft). */
  refundCredits(instanceId: string, projectId: number): Observable<void> {
    return this.flowService.refundMyCheckCredits(instanceId, projectId);
  }

  /**
   * Öffentlich, weil auch Duplikate und geteilte Einträge darüber abgebildet werden – von Hand
   * gebaute CheckDefinition-Literale sind eine Fehlerquelle (sie schleppen Felder der Vorlage mit).
   */
  toCheckDefinition(def: WorkflowDefinition): CheckDefinition {
    let description = def.description ?? '';
    let checkType: RulesetType = 'IDS';
    let ruleCount = 0;
    let startSelectionStep: number | null | undefined;

    if (def.manifestJson) {
      try {
        const manifest = JSON.parse(def.manifestJson);
        startSelectionStep = interactiveBuilderStep(manifest);
        description = description || manifest.description || '';
        // Derive checkType from manifest steps
        const steps = manifest.steps ?? [];
        if (steps.some((s: any) => ['hoai_rule_resolver', 'hoai_check', 'hoai_result_aggregator', 'ai_rule_resolver', 'ai_check', 'ai_result_aggregator'].includes(s.moduleId))) {
          checkType = 'KI';
        } else if (steps.some((s: any) => s.moduleId === 'HYBRID_RULESET_EXECUTOR')) {
          checkType = 'HYBRID';
        } else {
          // Check rule_selection settings for selectedRulesetType
          const ruleStep = steps.find((s: any) => s.moduleId === 'rule_selection');
          if (ruleStep?.settings?.selectedRulesetType) {
            checkType = ruleStep.settings.selectedRulesetType;
          } else if (ruleStep?.settings?.ruleTypes?.includes('HYBRID')) {
            checkType = 'HYBRID';
          } else if (ruleStep?.settings?.ruleTypes?.includes('KI')) {
            checkType = 'KI';
          }
        }
        ruleCount = this.deriveRuleCount(steps);
      } catch { /* ignore */
      }
    }
    return {
      id: def.id!,
      name: def.name ?? 'Unbenannter Check',
      slug: def.slug,
      description,
      version: def.version ?? '',
      status: (def.status as CheckDefinition['status']) ?? 'DRAFT',
      visibility: (def.visibility as 'PUBLIC' | 'PRIVATE') ?? 'PRIVATE',
      readOnly: def.readOnly ?? false,
      checkType,
      origin: def.visibility === 'PUBLIC' ? 'BIC' : 'CUSTOM',
      ruleCount,
      projectId: def.projectId,
      // Noch nicht im generierten Modell (src/app/api ist gitignored und wird erst mit laufendem
      // Backend neu erzeugt) – deshalb der Zugriff über die erweiterte Form.
      sharedProjects: (def as WorkflowDefinition & {sharedProjects?: SharedProject[]}).sharedProjects ?? undefined,
      createdAt: def.createdAt,
      updatedAt: def.updatedAt,
      // Ebenfalls noch nicht im generierten Modell – Katalog-Herkunft eines Duplikats.
      sourceCatalogKey: (def as WorkflowDefinitionWithCatalog).sourceCatalogKey ?? undefined,
      sourceCatalogVersion: (def as WorkflowDefinitionWithCatalog).sourceCatalogVersion ?? undefined,
      startSelectionStep,
    };
  }

  /**
   * Bildet eine Freigabe auf das Karten-Modell ab.
   *
   * <p>`id` trägt die **Definitions-ID der admin-eigenen Kopie** — sie ist nötig, damit der
   * Empfänger den Check ausführen kann (die Ausführung navigiert mit `definitionId`). Das ist
   * unbedenklich: die Kopie gehört keinem Kundenprojekt. `shareId` bleibt separat, weil das
   * Duplizieren über die Freigabe läuft.
   *
   * <p>`status` kommt vom Server und ist nicht hartverdrahtet: eine im Adminbereich unvollständig
   * gespeicherte Kopie steht auf `DRAFT` und ist dann bewusst nicht ausführbar.
   */
  private toSharedCheckDefinition(share: SharedMyCheck): CheckDefinition {
    return {
      id: share.definitionId,
      shareId: share.shareId,
      shared: true,
      name: share.name,
      description: share.description ?? '',
      version: share.version ?? '',
      status: (share.status as CheckDefinition['status']) ?? 'ACTIVE',
      visibility: 'PRIVATE',
      readOnly: true,
      checkType: 'IDS',
      origin: 'CUSTOM',
      ruleCount: share.ruleCount ?? 0,
      createdAt: share.sharedAt,
      catalogKey: share.catalogKey ?? undefined,
      catalogVersion: share.catalogVersion ?? undefined,
    };
  }

  /** Best-effort-Ermittlung der Anzahl an Prüfregeln aus den Manifest-Steps. */
  private deriveRuleCount(steps: any[]): number {
    const ruleStep = steps.find((s: any) => s.moduleId === 'rule_selection');
    const settings = ruleStep?.settings ?? {};
    const ruleIds = settings.selectedRuleIds ?? [];
    const ruleSetIds = settings.selectedRuleSetIds ?? [];
    return new Set([...ruleIds, ...ruleSetIds]).size;
  }
}
